import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmt, fmtCents } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, TrendingDown, TrendingUp, ArrowUpRight, FileText, Users } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis, YAxis } from "recharts";
import CategoryBudgets from "@/components/CategoryBudgets";
import { useHouseholdMode } from "@/hooks/useHouseholdMode";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [savingsSummary, setSavings] = useState(null);
  const { enabled: hhMode, param } = useHouseholdMode();

  useEffect(() => {
    (async () => {
      const [{ data: dash }, { data: sav }] = await Promise.all([
        api.get("/dashboard", { params: param }),
        api.get("/savings_actions"),
      ]);
      setData(dash);
      setSavings(sav);
    })();
  }, [hhMode]); // eslint-disable-line

  if (!data) return <div className="p-6 text-slate-400">Loading…</div>;

  const { cash_flow, potential_monthly_savings, categories, top_merchants, largest_increases, trend, moneywise_score } = data;

  return (
    <div className="px-6 pt-10 pb-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
            Hello, {user.name.split(" ")[0]}
            {hhMode && (
              <span data-testid="household-badge" className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                <Users size={10} /> Household
              </span>
            )}
          </div>
          <div className="font-manrope text-xl font-bold text-slate-900 tracking-tight">Your money this month</div>
        </div>
        <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-manrope font-bold text-sm">{user.name.slice(0, 1).toUpperCase()}</div>
      </div>

      {/* Hero KPI: Potential savings */}
      <div className="mt-8 rounded-3xl bg-slate-900 text-white p-6 relative overflow-hidden">
        <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Potential monthly savings</div>
        <div data-testid="hero-potential-savings" className="mt-2 font-manrope text-5xl sm:text-6xl font-extrabold tracking-tighter tabular-nums">
          {fmt(potential_monthly_savings)}
          <span className="text-slate-400 text-lg font-semibold">/mo</span>
        </div>
        <div className="text-slate-400 text-sm mt-1">≈ {fmt(potential_monthly_savings * 12)}/year</div>

        <Link to="/app/find-money" data-testid="find-money-btn" className="mt-6 group flex items-center justify-between gap-3 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full py-4 pl-6 pr-4 font-semibold transition-colors duration-200 active:scale-[0.98]">
          <div className="flex items-center gap-2">
            <Sparkles size={18} strokeWidth={2} />
            <span>FIND ME MONEY</span>
          </div>
          <ArrowUpRight className="transition-transform duration-200 group-hover:translate-x-0.5" size={20} />
        </Link>

        <Link to="/app/coach" data-testid="coach-cta-btn" className="mt-3 group flex items-center justify-between gap-3 w-full bg-white/10 hover:bg-white/15 text-white rounded-full py-3 pl-5 pr-4 font-medium transition-colors duration-200 border border-white/10">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-emerald-300">💬</span>
            <span>Ask the Money Coach</span>
          </div>
          <ArrowUpRight className="text-slate-300" size={16} />
        </Link>

        <Link to="/app/report" data-testid="report-cta-btn" className="mt-2 group flex items-center justify-between gap-3 w-full bg-white/10 hover:bg-white/15 text-white rounded-full py-3 pl-5 pr-4 font-medium transition-colors duration-200 border border-white/10">
          <div className="flex items-center gap-2 text-sm">
            <FileText size={14} className="text-emerald-300" />
            <span>Monthly report</span>
          </div>
          <ArrowUpRight className="text-slate-300" size={16} />
        </Link>
      </div>

      {/* KPI trio */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        <Kpi label="Income" value={cash_flow.income} tone="pos" testId="kpi-income" />
        <Kpi label="Expenses" value={cash_flow.expenses} tone="neg" testId="kpi-expenses" />
        <Kpi label="Net cash flow" value={cash_flow.net} tone={cash_flow.net >= 0 ? "pos" : "neg"} testId="kpi-net" />
      </div>

      {/* MoneyWise score */}
      <div className="mt-6 rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">MoneyWise Score</div>
          <div data-testid="mw-score" className="font-manrope text-3xl font-extrabold tracking-tighter text-slate-900 tabular-nums">{moneywise_score.score}<span className="text-slate-300 text-lg">/100</span></div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${moneywise_score.score}%` }} />
        </div>
        <ul className="mt-4 space-y-1.5">
          {moneywise_score.reasons.map((r, i) => <li key={i} className="text-sm text-slate-600">• {r}</li>)}
        </ul>
      </div>

      {/* Savings plan progress */}
      {savingsSummary && savingsSummary.identified_monthly > 0 && (
        <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Your savings plan</div>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <MiniStat label="Identified" value={savingsSummary.identified_monthly} />
            <MiniStat label="Achieved" value={savingsSummary.achieved_monthly} strong />
            <MiniStat label="Remaining" value={savingsSummary.remaining_monthly} />
          </div>
          <Link to="/app/find-money" className="mt-4 inline-block text-sm font-semibold text-slate-900 underline underline-offset-2">Review plan →</Link>
        </div>
      )}

      {/* 6-month trend */}
      <div className="mt-6 rounded-2xl border border-slate-200 p-5">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Income vs expenses</div>
        <div className="h-40 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="expenses" stroke="#0f172a" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Categories */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Spending by category</div>
        <div className="mt-3 space-y-2">
          {categories.slice(0, 6).map((c) => (
            <div key={c.category} className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
              <div>
                <div className="font-medium text-slate-900 text-sm">{c.category}</div>
                <div className="text-xs text-slate-400">{c.transaction_count} transactions</div>
              </div>
              <div className="text-right">
                <div className="font-manrope font-bold text-slate-900 tabular-nums">{fmtCents(c.current_month)}</div>
                {c.avg_6mo > 0 && (
                  <div className={`text-xs flex items-center gap-1 justify-end ${c.pct_change_vs_avg6 > 15 ? "text-red-500" : c.pct_change_vs_avg6 < -5 ? "text-emerald-600" : "text-slate-400"}`}>
                    {c.pct_change_vs_avg6 > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {c.pct_change_vs_avg6 > 0 ? "+" : ""}{c.pct_change_vs_avg6.toFixed(0)}% vs 6mo avg
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Budgets */}
      <CategoryBudgets />

      {/* Top merchants */}
      {top_merchants.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Top merchants</div>
          <div className="mt-3 space-y-2">
            {top_merchants.map((m) => (
              <div key={m.merchant} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <div className="text-sm font-medium text-slate-900">{m.merchant}</div>
                  <div className="text-xs text-slate-400">{m.count}× this month</div>
                </div>
                <div className="font-manrope font-bold text-slate-900 tabular-nums">{fmtCents(m.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {largest_increases.length > 0 && (
        <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-5">
          <div className="text-xs uppercase tracking-widest text-red-600 font-semibold">Biggest spending increases</div>
          <div className="mt-3 space-y-2">
            {largest_increases.map((i) => (
              <div key={i.category} className="flex justify-between items-baseline">
                <span className="text-slate-800 font-medium">{i.category}</span>
                <span className="text-sm text-red-600 font-semibold">+{fmt(i.current_month - i.avg_6mo)} vs 6mo avg</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone, testId }) {
  const color = tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-slate-900" : "text-slate-900";
  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div data-testid={testId} className={`mt-1 font-manrope text-lg font-extrabold tracking-tight tabular-nums ${color}`}>{fmt(value)}</div>
    </div>
  );
}

function MiniStat({ label, value, strong }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{label}</div>
      <div className={`font-manrope font-extrabold tracking-tight tabular-nums ${strong ? "text-emerald-700 text-xl" : "text-slate-900 text-lg"}`}>{fmt(value)}</div>
    </div>
  );
}
